import classnames from 'classnames'
import {useState, Fragment} from 'react'
import {useMutation} from '@apollo/client'
import {useForm, FormProvider, Controller} from 'react-hook-form'
import flags from 'react-phone-number-input/flags'
import {isValidPhoneNumber, formatPhoneNumber} from 'react-phone-number-input'
import {List, Map} from 'immutable'
import PropTypes from 'prop-types'

import PhoneInput from 'react-phone-number-input/react-hook-form'
import {FormControlLabel, Radio, RadioGroup, FormHelperText, Stack} from '@mui/material'
import IconButton from '@mui/material/IconButton'

import {Button, Cancel, Remove} from '../../../../../dashboard/src/components/blocks/Buttons'
import ButtonGroup from '../../../../../shared_components/core/buttons/ButtonGroup'
import {ContentBoxGutterMedium, ContentBox} from '../../../../../dashboard/src/components/blocks/ContentBoxes'
import ManageTfaSetupFrame from '../ManageTfaSetupFrame'
import MediumModal from '../../../../../shared_components/mui_base_components/modals/medium_modal/MediumModal'
import OtpInput from '../../../../../shared_components/forms/v3/OtpInput'
import SmartphoneIcon from '../tfa_setup/SmartphoneIcon'
import {TextGutterMedium, TextGutterLarge} from '../../../../../dashboard/src/components/blocks/Texts'
import SuccessMessage from './SuccessMessage'

import {TFA_ENROLLMENTS} from '../../../../../graphql/queries/tfa'
import {CREATE_TFA_PHONE_NUMBER, CONFIRM_PRIMARY_TFA_NUMBER, DELETE_TFA_PHONE_NUMBER, UPDATE_TFA_PHONE_NUMBER_MESSAGE_MODE} from '../../../../../graphql/mutations/tfa'
import {cleanGraphQLResponse} from '../../../../../lib/graphQLTools'
import TfaApi from '../../../web/TfaApi'

const ManagePhoneEnrollment = props => {
  const [status, setStatus] = useState('initial')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const methods = useForm({
    defaultValues: {
      number: props.tfaConfig.getIn(['primary_number', 'number'], ''),
      secondaryNumber: props.tfaConfig.getIn(['secondary_number', 'number'], '')
    }
  })
  const [createTfaPhoneNumber, {data}] = useMutation(CREATE_TFA_PHONE_NUMBER, {
    awaitRefetchQueries: true,
    refetchQueries: [{query: TFA_ENROLLMENTS}]
  })
  const [confirmPrimaryTfaNumber] = useMutation(CONFIRM_PRIMARY_TFA_NUMBER)
  const [deleteTfaPhoneNumber] = useMutation(DELETE_TFA_PHONE_NUMBER)
  const [updateTfaPhoneNumberMessageMode] = useMutation(UPDATE_TFA_PHONE_NUMBER_MESSAGE_MODE)
  const tfaPhoneNumber = cleanGraphQLResponse(data, 'createTfaPhoneNumber', Map())
  // Only a persisted primary phone can have its message mode updated, so a
  // delivery-method change is actionable only once primary_number.id exists.
  const savedPreferredMessageMode = props.tfaConfig.getIn(['primary_number', 'preferred_message_mode'], 'text')
  const messageModeChanged = !!props.tfaConfig.getIn(['primary_number', 'id']) &&
    !!methods.watch('preferredMessageMode') &&
    methods.watch('preferredMessageMode') !== savedPreferredMessageMode

  const createPhoneNumber = ({input, onCompleted}) => {
    createTfaPhoneNumber({variables: {input}, onCompleted, onError: () => setLoading(false)})
  }

  const toggleModal = () => {
    if (showModal && status === 'success') setStatus('initial')

    setShowModal(!showModal)
    setLoading(false)
  }

  const createPrimaryPhoneNumber = ({number, preferredMessageMode}) => {
    setLoading(true)
    const input = {
      tfaConfigId: props.tfaConfig.get('id'),
      preferredMessageMode,
      primary: true,
      number,
      nationalNumber: formatPhoneNumber(number)
    }
    createPhoneNumber({input, onCompleted: () => {
      setLoading(false)
      if (status !== 'confirm') setStatus('confirm')
    }})
  }

  const createSecondaryPhoneNumber = ({number, preferredMessageMode, onCompleted}) => {
    const input = {
      tfaConfigId: props.tfaConfig.get('id'),
      preferredMessageMode,
      primary: false,
      number,
      nationalNumber: formatPhoneNumber(number)
    }
    createPhoneNumber({input, onCompleted})
  }

  const removeSecondaryPhoneNumber = ({onCompleted}) => {
    deleteTfaPhoneNumber({
      variables: {phoneId: props.tfaConfig.getIn(['secondary_number', 'id'])},
      onCompleted,
      onError: () => setLoading(false)
    })
  }

  const updatePrimaryMessageMode = ({preferredMessageMode, onCompleted}) => {
    updateTfaPhoneNumberMessageMode({
      variables: {input: {id: props.tfaConfig.getIn(['primary_number', 'id']), preferredMessageMode}},
      onCompleted,
      onError: () => setLoading(false)
    })
  }

  const confirmPrimaryNumber = ({confirmCode}) => {
    setError('')

    confirmPrimaryTfaNumber({
      variables: {input: {confirmCode}, phoneId: tfaPhoneNumber.get('id')},
      onCompleted: () => {
        const tfaConfigId = props.tfaConfig.get('id')
        const payload = {tfaConfigId, enabled: true, enrollmentId: props.phoneEnrollment.get('id')}

        props.toggleTfaStatus({
          variables: {input: payload},
          onCompleted: () => {
            if (props.tfaConfig.get('status') !== 'enabled') {
              props.generateBackupCodes(() => {
                TfaApi.fetchTfaConfig(tfaConfigId)
                props.manageTfaSetup('recovery')
              })
            } else {
              setStatus('success')
              toggleModal()
              TfaApi.fetchTfaConfig(tfaConfigId)
            }
          }
        })
      },
      onError: () => {
        setError('The code you entered is incorrect. Please try again.')
        setLoading(false)
      }
    })
  }

  const submitSecondaryAndConfirmPrimaryNumbers = ({secondaryNumber, confirmCode, preferredMessageMode}) => {
    setLoading(true)
    if (confirmCode)
      confirmPrimaryNumber({confirmCode})

    const savedSecondaryNumber = props.tfaConfig.getIn(['secondary_number', 'number'])
    const persistMessageMode = !confirmCode && messageModeChanged
    const onCompleted = () => {
      TfaApi.fetchTfaConfig(props.tfaConfig.get('id'))
      props.toggleModal()
    }
    // When the secondary number also changes, save the message mode after that
    // mutation completes so a single save persists both changes.
    const finishWithMessageMode = persistMessageMode ?
      () => updatePrimaryMessageMode({preferredMessageMode, onCompleted}) :
      onCompleted

    if (secondaryNumber && secondaryNumber !== savedSecondaryNumber)
      createSecondaryPhoneNumber({number: secondaryNumber, preferredMessageMode, onCompleted: finishWithMessageMode})
    else if (!secondaryNumber && savedSecondaryNumber)
      removeSecondaryPhoneNumber({onCompleted: finishWithMessageMode})
    else if (persistMessageMode)
      finishWithMessageMode()
  }

  const handleDelete = () => {
    setDeleting(true)
    props.onDelete({id: props.phoneEnrollment.get('id'),
      onCompleted: () => {
        TfaApi.fetchTfaConfig(props.tfaConfig.get('id'))
        setDeleting(false)
        toggleModal()
        props.toggleModal()
      }
    })
  }

  const onFinish = () => {
    props.toggleModal()
    setStatus('initial')
  }

  const resendCode = () => {
    const [number, preferredMessageMode] = methods.getValues(['number', 'preferredMessageMode'])

    createPrimaryPhoneNumber({number, preferredMessageMode})
  }

  const handleEnterKey = event => {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Route through handleSubmit so the form validates (and surfaces an
      // incomplete-code error) on Enter, matching the Save button's behavior.
      methods.handleSubmit(submitSecondaryAndConfirmPrimaryNumbers)()
    }
  }

  return (
    <Fragment>
      <ManageTfaSetupFrame
        headerIcon={<SmartphoneIcon />}
        headerTitle={`${props.phoneEnrollment.isEmpty() ? 'Add' : 'Manage'} phone (SMS/Voice)`}
        headerSubtitle={`${props.phoneEnrollment.isEmpty() ? 'Configure' : 'Review or update'} your SMS authentication preferences.`}
        removeText={(props.phoneEnrollment.isEmpty() || !props.phoneEnrollment.get('enabled-at') || props.tfaRequired) ? null : 'Remove authentication method'}
        primaryAction={toggleModal}>
        <ContentBoxGutterMedium className='phone-container body'>
          <ContentBoxGutterMedium className='phone-input-container'>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(createPrimaryPhoneNumber)}>
                <Fragment>
                  <h5>Preferred delivery method</h5>
                  <Controller
                    name='preferredMessageMode'
                    control={methods.control}
                    defaultValue={props.tfaConfig.getIn(['primary_number', 'preferred_message_mode'], 'text')}
                    render={({field}) => (
                      <RadioGroup {...field} className='verification-options' aria-label='Preferred delivery method'>
                        <FormControlLabel
                          value='text'
                          control={<Radio />}
                          label={
                            <Stack>
                              <span>Text message (SMS)</span>
                              <FormHelperText component='span'>Receive a code via text message</FormHelperText>
                            </Stack>
                          }
                        />
                        <FormControlLabel
                          value='voice'
                          control={<Radio />}
                          label={
                            <Stack>
                              <span>Phone call (Voice)</span>
                              <FormHelperText component='span'>Receive a code via automated call</FormHelperText>
                            </Stack>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                  <h5 className='phone-label'>Phone number</h5>
                  <PhoneInput
                    name='number'
                    control={methods.control}
                    numberInputProps={{
                      className: 'phone-number',
                      placeholder: '(555) 555-5555',
                      autoFocus: true
                    }}
                    flags={flags}
                    defaultCountry='US'
                    rules={{
                      required: 'Phone number is required',
                      validate: isValidPhoneNumber
                    }}
                    limitMaxLength={true}
                    initialValueFormat='national'
                  />
                  <TextGutterLarge className='phone-input-helper-text'>
                    We'll send a verification code to this number
                  </TextGutterLarge>
                  {
                    status === 'initial' && (
                      <Button
                        className={classnames('submit-phone-number', {disabled: loading})}
                        disabled={loading}
                        onClick={methods.handleSubmit(createPrimaryPhoneNumber)}>
                        <SmartphoneIcon /><span>Send confirmation code</span>
                      </Button>
                    )
                  }
                  {
                    props.tfaConfig.get('secondary_number') && (
                      <Fragment>
                        <h5 className='phone-label'>Secondary phone number</h5>
                        <PhoneInput
                          name='secondaryNumber'
                          control={methods.control}
                          numberInputProps={{
                            className: 'phone-number',
                            placeholder: '(555) 555-5555'
                          }}
                          flags={flags}
                          defaultCountry='US'
                          rules={{
                            validate: value => !value || isValidPhoneNumber(value)
                          }}
                          limitMaxLength={true}
                          initialValueFormat='national'
                        />
                        <TextGutterLarge>A backup number for sending the verification code</TextGutterLarge>
                      </Fragment>
                    )
                  }
                </Fragment>
                {
                  status === 'confirm' && (
                    <ContentBox className='otp-input-container flex-container'>
                      <h5>Verification code</h5>
                      <OtpInput className='otp-input' name='confirmCode' control={methods.control} error={error} onKeyDown={handleEnterKey} />
                      <TextGutterMedium className='flex-container'>
                        Didn't receive a code? <IconButton onClick={resendCode} className='resend'>Resend</IconButton>
                      </TextGutterMedium>
                    </ContentBox>
                  )
                }
              </form>
            </FormProvider>
          </ContentBoxGutterMedium>
          <ButtonGroup>
            <Cancel onClick={props.toggleModal} />
            <Button
              processing={loading}
              disabled={
                props.tfaConfig.getIn(['primary_number', 'number'], '') === methods.watch('number') &&
                props.tfaConfig.getIn(['secondary_number', 'number'], '') === methods.watch('secondaryNumber') &&
                !messageModeChanged && !methods.watch('confirmCode') ||
                status === 'confirm' && methods.watch('confirmCode')?.length !== 6 ||
                status === 'initial' && props.tfaConfig.getIn(['primary_number', 'number'], '') !== methods.watch('number')
              }
              onClick={methods.handleSubmit(submitSecondaryAndConfirmPrimaryNumbers)}>
                Save
            </Button>
          </ButtonGroup>
        </ContentBoxGutterMedium>
      </ManageTfaSetupFrame>
      <MediumModal className='warning-modal phone-enrollment' open={showModal} onClose={props.toggleModal}>
        <ContentBox className='content flex-container'>
          {
            status === 'success' ?
              (
                <SuccessMessage mode='phone' onFinish={onFinish} />
              ) :
              (
                <Fragment>
                  <h2>Remove this authentication method?</h2>
                  <TextGutterMedium>
                    {
                      props.tfaEnrollments.size > 1 ?
                        'This will remove phone (SMS/Voice) verification. Your account is still protected by your authenticator app.' :
                        'Without two-factor authentication, your password is the only thing protecting your account.'
                    }
                  </TextGutterMedium>
                  <ButtonGroup>
                    <Cancel disabled={deleting} onClick={toggleModal} />
                    <Remove processing={deleting} onClick={handleDelete}>Continue</Remove>
                  </ButtonGroup>
                </Fragment>
              )
          }
        </ContentBox>
      </MediumModal>
    </Fragment>
  )
}

ManagePhoneEnrollment.propTypes = {
  backupCodes: PropTypes.instanceOf(List).isRequired,
  tfaConfig: PropTypes.instanceOf(Map).isRequired,
  phoneEnrollment: PropTypes.instanceOf(Map).isRequired,
  removeTfaEnrollment: PropTypes.func.isRequired,
  tfaEnrollments: PropTypes.instanceOf(List).isRequired,
  manageTfaSetup: PropTypes.func,
  generateBackupCodes: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  toggleTfaStatus: PropTypes.func.isRequired,
  tfaRequired: PropTypes.bool
}

export default ManagePhoneEnrollment
